import React, { useEffect, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import FileDropZone from 'tools/FileDropZone';
import {
  btnRow,
  card,
  chipStyle,
  errorBox,
  formatMb,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  warnBox,
} from 'tools/toolStyles';
import { downloadBlob, getWin, replaceExt, revokeUrl } from 'tools/ffmpegShared';

type OutFmt = 'png' | 'jpeg' | 'webp';

type ResultItem = {
  id: string;
  name: string;
  blob: any;
  url: string;
  note?: string;
};

const MIME: { [k in OutFmt]: string } = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

const EXT: { [k in OutFmt]: string } = {
  png: 'png',
  jpeg: 'jpg',
  webp: 'webp',
};

function loadImage(file: any): Promise<any> {
  const win = getWin();
  return new Promise((resolve, reject) => {
    const url = win.URL.createObjectURL(file);
    const img = new win.Image();
    img.onload = () => {
      resolve({ img, url });
    };
    img.onerror = () => {
      win.URL.revokeObjectURL(url);
      reject(new Error(`Không đọc được ảnh: ${file.name}`));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: any, type: string, quality: number): Promise<any> {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b: any) => resolve(b), type, quality);
    } else {
      try {
        const dataUrl = canvas.toDataURL(type, quality);
        const win = getWin();
        const bin = win.atob(dataUrl.split(',')[1]);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(new win.Blob([arr], { type }));
      } catch {
        resolve(null);
      }
    }
  });
}

async function convertOne(file: any, fmt: OutFmt, quality: number): Promise<ResultItem> {
  const win = getWin();
  const { img, url } = await loadImage(file);
  try {
    const canvas = win.document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas không khả dụng.');
    if (fmt === 'jpeg') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(img, 0, 0);

    let note: string | undefined;
    let outFmt = fmt;
    let blob = await canvasToBlob(canvas, MIME[fmt], quality);
    if (!blob && fmt === 'webp') {
      outFmt = 'png';
      blob = await canvasToBlob(canvas, MIME.png, 1);
      note = 'Trình duyệt không hỗ trợ WebP → đã xuất PNG.';
    }
    if (!blob) throw new Error('Không tạo được file đầu ra.');

    const name = replaceExt(file.name, EXT[outFmt]);
    const outUrl = win.URL.createObjectURL(blob);
    return { id: `${file.name}-${Date.now()}-${Math.random()}`, name, blob, url: outUrl, note };
  } finally {
    revokeUrl(url);
  }
}

const ConvertImage = () => {
  const [files, setFiles] = useState<any[]>([]);
  const [fmt, setFmt] = useState<OutFmt>('png');
  const [quality, setQuality] = useState(0.9);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<ResultItem[]>([]);

  useEffect(() => {
    return () => {
      results.forEach((r) => revokeUrl(r.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFiles = (list: any[]) => {
    setFiles(list || []);
    setError('');
    results.forEach((r) => revokeUrl(r.url));
    setResults([]);
  };

  const run = async () => {
    if (!files.length) {
      setError('Hãy chọn ít nhất một ảnh.');
      return;
    }
    setBusy(true);
    setError('');
    results.forEach((r) => revokeUrl(r.url));
    setResults([]);
    const next: ResultItem[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        next.push(await convertOne(files[i], fmt, quality));
      }
      setResults(next);
    } catch (err: any) {
      next.forEach((r) => revokeUrl(r.url));
      setError((err && err.message) || 'Chuyển đổi thất bại.');
    } finally {
      setBusy(false);
    }
  };

  const downloadAll = () => {
    results.forEach((r, i) => {
      setTimeout(() => downloadBlob(r.blob, r.name), i * 200);
    });
  };

  const needQuality = fmt === 'jpeg' || fmt === 'webp';
  const large = files.some((f) => f.size > 20 * 1024 * 1024);

  return (
    <ToolLayout title="Đổi đuôi ảnh">
      <p style={privacy}>
        Ảnh được xử lý hoàn toàn trên máy bạn (Canvas API), không gửi lên server language123.net.
      </p>

      <div style={card}>
        <label style={label}>1. Chọn ảnh (có thể nhiều file)</label>
        <p style={hint}>PNG / JPG / WebP / GIF (khung đầu) / BMP / SVG.</p>
        <FileDropZone
          accept="image/*,.svg"
          multiple
          hintText="Kéo thả hoặc bấm để chọn nhiều ảnh"
          onFiles={applyFiles}
        />
        {files.length ? (
          <p style={{ ...hint, marginTop: 8, marginBottom: 0 }}>
            {files.length} file · tổng {formatMb(files.reduce((s, f) => s + f.size, 0))} MB
          </p>
        ) : null}
        {large ? <p style={{ ...warnBox, marginTop: 12 }}>Một số file &gt; 20MB — trình duyệt có thể chậm.</p> : null}
      </div>

      <div style={card}>
        <label style={label}>2. Định dạng xuất</label>
        <div style={btnRow}>
          <button type="button" style={chipStyle(fmt === 'png')} onClick={() => setFmt('png')}>
            PNG
          </button>
          <button type="button" style={chipStyle(fmt === 'jpeg')} onClick={() => setFmt('jpeg')}>
            JPG
          </button>
          <button type="button" style={chipStyle(fmt === 'webp')} onClick={() => setFmt('webp')}>
            WebP
          </button>
        </div>
        {fmt === 'webp' ? (
          <p style={hint}>Firefox có thể không encode WebP — tool sẽ tự fallback PNG.</p>
        ) : null}
        {needQuality ? (
          <>
            <label style={label}>Chất lượng: {Math.round(quality * 100)}%</label>
            <input
              type="range"
              min={0.5}
              max={1}
              step={0.05}
              value={quality}
              onChange={(e: any) => setQuality(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </>
        ) : null}
      </div>

      <div style={card}>
        <div style={btnRow}>
          <button type="button" style={primaryBtn} disabled={!files.length || busy} onClick={run}>
            {busy ? 'Đang chuyển…' : 'Chuyển đổi'}
          </button>
          {results.length ? (
            <button type="button" style={secondaryBtn} onClick={downloadAll}>
              Tải tất cả
            </button>
          ) : null}
        </div>
        {error ? <p style={errorBox}>{error}</p> : null}
        {results.map((r) => (
          <div
            key={r.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 12,
              padding: 8,
              background: '#fafafa',
              borderRadius: 8,
            }}
          >
            <img src={r.url} alt={r.name} style={{ width: 64, height: 64, objectFit: 'contain', background: '#eee' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, wordBreak: 'break-all' }}>{r.name}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{formatMb(r.blob.size)} MB{r.note ? ` · ${r.note}` : ''}</div>
            </div>
            <button type="button" style={primaryBtn} onClick={() => downloadBlob(r.blob, r.name)}>
              Tải
            </button>
          </div>
        ))}
      </div>
    </ToolLayout>
  );
};

export default ConvertImage;
